/* eslint-disable react-portal/no-window-location-assign */
/* eslint-disable react-portal/no-react-router-use-navigate */
/* eslint-disable react-portal/no-react-router-navigate */
import { createElement, useCallback, useMemo } from 'react';
import {
    useParams,
    useNavigate as useRouterNavigate,
    useLocation,
    resolvePath,
    Navigate as RouterNavigate,
} from 'react-router-dom';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useScopedPlmId } from '../components/links/PlmContext';

export const PLM_HOME = 'plm';
export const PLM_ROOT = `/${PLM_HOME}`;

export function addPlmPrefix(url, plmId) {
    // Nothing to do if we don't have a plmId.
    if (!plmId) return url;

    const plmPrefix = `${PLM_ROOT}/${plmId}`;

    // Nothing to do if the URL is already prefixed.
    if (url.startsWith(plmPrefix)) return url;

    // Ensure we don't have two slashes between the prefix and target URL,
    // since that can affect how we can match active routes.
    // i.e., /plm/12345//sites-overview
    const urlSeparator = url.startsWith('/') ? '' : '/';

    return `${plmPrefix}${urlSeparator}${url}`;
}

export function removePlmPrefix(url) {
    const prefixPattern = /^\/plm\/([0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12})/i;
    return url.replace(prefixPattern, '');
}

export function assignLocation(url, plmId) {
    window.location.assign(addPlmPrefix(url, plmId));
}

/**
 * @returns {string|null}
 */
export function usePlmId() {
    const scopedPlmId = useScopedPlmId();
    const { plmId } = useParams();

    return plmId || scopedPlmId;
}

export function usePlmPath(url, plmIdDefault) {
    const plmId = usePlmId() || plmIdDefault;

    return useMemo(() => addPlmPrefix(url, plmId), [url, plmId]);
}

export function useNavigate() {
    const location = useLocation();
    const plmId = usePlmId();
    const routerNavigate = useRouterNavigate();

    return useCallback(
        (toOrDelta, options) => {
            // Don't add a prefix if the first argument is a delta (i.e. -1 or 1).
            if (_.isNumber(toOrDelta)) return routerNavigate(toOrDelta, options);

            // We need to resolve the path relative to the current location in case it's a relative path.
            const resolvedPath = resolvePath(toOrDelta, location.pathname);

            // Add the PLM prefix to the resolved path.
            return routerNavigate({ ...resolvedPath, pathname: addPlmPrefix(resolvedPath.pathname, plmId) }, options);
        },
        [routerNavigate, location, plmId]
    );
}

/**
 * Wrapper over the react-router-dom Navigate component
 * to include PLM IDs
 */
export function Navigate(props) {
    const { to: initialTo, plmId, ...rest } = props;
    const to = usePlmPath(initialTo, plmId);

    return createElement(RouterNavigate, { to, ...rest });
}

Navigate.propTypes = { ...RouterNavigate.propTypes, plmId: PropTypes.string };
Navigate.defaultProps = { plmId: null };

export function useAssignLocation() {
    const plmId = usePlmId();
    return useCallback(
        (url) => {
            assignLocation(url, plmId);
        },
        [plmId]
    );
}
